/**
 * Product card image slider.
 *
 * Drives the markup from snippets/product-listing-slider.liquid. Sliding itself is
 * native CSS scroll snapping, so touch swiping and trackpads work without JS and
 * keep their platform feel. This adds what native scrolling does not give:
 *
 *   - dot pagination kept in sync with the scroll position, and clickable
 *   - pointer dragging for mouse users, including suppressing the card link when
 *     the pointer was dragged rather than clicked
 *   - dismissing the colour swatch layer, which would otherwise cover the slides
 *   - lifting the dots clear of the quick add panel while it is showing
 */
(function () {
  "use strict";

  var selectors = {
    slider: '.js-product-slider',
    track: '.js-product-slider-track',
    dots: '.js-product-slider-dots',
    dot: '.product-slider__dot',
    listing: '.js-product-listing',
    imageWrapper: '.prod-image',
    quickAdd: '.product--grid_add'
  };

  var classes = {
    active: 'is-active',
    dragging: 'is-dragging',
    sliding: 'is-sliding'
  };

  // Pointer travel, in px, past which a gesture counts as a drag and not a click.
  var DRAG_THRESHOLD = 8;

  /**
   * @description Index of the slide currently snapped into view.
   * @param  {HTMLElement} track Scrolling element.
   * @return {Number} Zero based slide index.
   */
  function _currentIndex(track) {
    var width = track.clientWidth;

    return width ? Math.round(track.scrollLeft / width) : 0;
  }

  /**
   * @description Mark the dot matching the visible slide.
   * @param  {HTMLElement} track Scrolling element.
   * @param  {NodeList} dots Dot buttons.
   */
  function _syncDots(track, dots) {
    var index = _currentIndex(track);

    dots.forEach(function (dot, position) {
      var isActive = position === index;

      dot.classList.toggle(classes.active, isActive);

      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  /**
   * @description Flag the card as being slid, which the stylesheet uses to hold
   * back the theme's secondary image hover.
   *
   * That hover shows the product's second image over the card, so left alone it
   * would sit on top of whichever slide the visitor scrolled to, and on desktop
   * it would cover the slider for the whole time the pointer is over the card.
   * Held back once the visitor moves off the first slide, or while dragging, so
   * on the first slide it still behaves exactly as the theme intends.
   *
   * @param  {HTMLElement} wrapper The .prod-image element.
   * @param  {HTMLElement} track Scrolling element.
   * @param  {Boolean} dragging Whether a drag is in progress.
   */
  function _updateSlidingState(wrapper, track, dragging) {
    wrapper.classList.toggle(classes.sliding, dragging || _currentIndex(track) !== 0);
  }

  /**
   * @description Scroll a slide into view.
   * @param  {HTMLElement} track Scrolling element.
   * @param  {Number} index Slide to show.
   * @param  {Boolean} smooth Whether to animate.
   */
  function _scrollToSlide(track, index, smooth) {
    track.scrollTo({
      left: index * track.clientWidth,
      behavior: smooth ? 'smooth' : 'auto'
    });
  }

  /**
   * @description Hand the card image back to the slider, in case a colour swatch
   * layer is currently covering it.
   * @param  {HTMLElement} listing A .js-product-listing element.
   */
  function _dismissSwatchLayer(listing) {
    if (window.WAU && window.WAU.GridSwatchImage) {
      window.WAU.GridSwatchImage.dismiss(listing);
    }
  }

  /**
   * @description Publish the quick add panel's height so the stylesheet can lift
   * the dots above it while it is showing. Measured rather than hard coded, the
   * panel's height depends on how many swatches a product has.
   * @param  {HTMLElement} wrapper The .prod-image element holding the dots.
   * @param  {HTMLElement} listing A .js-product-listing element.
   */
  function _publishQuickAddHeight(wrapper, listing) {
    var panel = listing.querySelector(selectors.quickAdd);

    // display:none below the theme's quick add breakpoint, which reads as 0.
    var height = panel ? panel.offsetHeight : 0;

    wrapper.style.setProperty('--grid-add-height', height + 'px');
  }

  /**
   * @description Wire up one slider.
   * @param  {HTMLElement} slider A .js-product-slider element.
   */
  function _initSlider(slider) {
    var track = slider.querySelector(selectors.track);
    var listing = slider.closest(selectors.listing);
    var wrapper = slider.closest(selectors.imageWrapper);

    if (!track || !listing || !wrapper) {
      return;
    }

    var dotList = wrapper.querySelector(selectors.dots);
    var dots = dotList ? dotList.querySelectorAll(selectors.dot) : [];
    var scrollQueued = false;

    // Not gated on there being dots, the secondary hover state depends on it too.
    track.addEventListener('scroll', function () {
      if (scrollQueued) {
        return;
      }

      scrollQueued = true;
      window.requestAnimationFrame(function () {
        scrollQueued = false;

        if (dots.length) {
          _syncDots(track, dots);
        }

        _updateSlidingState(wrapper, track, track.classList.contains(classes.dragging));
      });
    });

    if (dots.length) {
      dotList.addEventListener('click', function (event) {
        var dot = event.target.closest(selectors.dot);

        if (!dot) {
          return;
        }

        _dismissSwatchLayer(listing);
        _scrollToSlide(track, parseInt(dot.getAttribute('data-slide-index'), 10), true);
      });

      _publishQuickAddHeight(wrapper, listing);
    }

    _initDragging(track, listing, wrapper);
  }

  /**
   * @description Let mouse users drag the track. Touch is left to native
   * scrolling, which already handles it better than JS can.
   * @param  {HTMLElement} track Scrolling element.
   * @param  {HTMLElement} listing A .js-product-listing element.
   * @param  {HTMLElement} wrapper The .prod-image element.
   */
  function _initDragging(track, listing, wrapper) {
    var pointerId = null;
    var startX = 0;
    var startScroll = 0;
    var travelled = 0;

    function _end() {
      if (pointerId === null) {
        return;
      }

      pointerId = null;
      track.style.scrollSnapType = '';
      track.classList.remove(classes.dragging);

      // Snapping is off during the drag, so land on a slide by hand.
      _scrollToSlide(track, _currentIndex(track), true);
      _updateSlidingState(wrapper, track, false);
    }

    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'touch' || !event.isPrimary) {
        return;
      }

      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      travelled = 0;

      // Snapping fights scrollLeft while dragging.
      track.style.scrollSnapType = 'none';
    });

    track.addEventListener('pointermove', function (event) {
      if (pointerId === null || event.pointerId !== pointerId) {
        return;
      }

      var distance = event.clientX - startX;

      travelled = Math.max(travelled, Math.abs(distance));

      if (travelled > DRAG_THRESHOLD) {
        track.classList.add(classes.dragging);
        _updateSlidingState(wrapper, track, true);
        _dismissSwatchLayer(listing);
        event.preventDefault();
      }

      track.scrollLeft = startScroll - distance;
    });

    track.addEventListener('pointerup', _end);
    track.addEventListener('pointercancel', _end);
    track.addEventListener('pointerleave', _end);

    // A drag ends with a click on the card link, which must not navigate.
    track.addEventListener('click', function (event) {
      if (travelled > DRAG_THRESHOLD) {
        event.preventDefault();
        event.stopPropagation();
      }

      travelled = 0;
    }, true);
  }

  /**
   * @description Initialise every slider not yet wired up.
   */
  function init() {
    document.querySelectorAll(selectors.slider).forEach(function (slider) {
      if (slider.dataset.sliderReady) {
        return;
      }

      slider.dataset.sliderReady = 'true';
      _initSlider(slider);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.WAU = window.WAU || {};
  window.WAU.ProductGridSlider = { init: init };
})();
